import ExpandMore from '@mui/icons-material/ExpandMore';
import Refresh from '@mui/icons-material/Refresh';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AiJobSummaryVerdict } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import Label from '~components/FormFields/v2/Label';
import { AiJobSummary } from '~hooks/useAiSummaries/models';

import { formatAiSummaryMinutes, formatAiSummaryTimestamp } from './aiSummaryFormatters';

const VERDICT_LABEL_KEYS: Record<AiJobSummaryVerdict, string> = {
  [AiJobSummaryVerdict.PENDING]: 'admin.job_geo_debug.ai_summary_verdict_pending',
  [AiJobSummaryVerdict.PASS]: 'admin.job_geo_debug.ai_summary_verdict_pass',
  [AiJobSummaryVerdict.PASS_WITH_WARNINGS]:
    'admin.job_geo_debug.ai_summary_verdict_pass_with_warnings',
  [AiJobSummaryVerdict.FAILED]: 'admin.job_geo_debug.ai_summary_verdict_failed',
  [AiJobSummaryVerdict.SKIPPED_MIN_PINGS]:
    'admin.job_geo_debug.ai_summary_verdict_skipped_min_pings',
};

interface AiSummaryFieldListProps {
  aiJobSummary: AiJobSummary | null;
  aiLoadSummaryCount: number;
  generationErrorMessage: string | null;
  isGenerating: boolean;
  isLoading: boolean;
  onGenerate: () => void;
  platformLoadCount: number;
}

function SummaryField({
  label,
  value,
  emphasizeError = false,
}: {
  label: string;
  value: string;
  emphasizeError?: boolean;
}) {
  return (
    <Box>
      <FormFieldLabel label={label} sx={{ mb: 0.5 }} />
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.75rem',
          ...(emphasizeError ? { color: 'error.main', fontWeight: 600 } : {}),
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function AiSummaryFieldList({
  aiJobSummary,
  aiLoadSummaryCount,
  generationErrorMessage,
  isGenerating,
  isLoading,
  onGenerate,
  platformLoadCount,
}: AiSummaryFieldListProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!aiJobSummary) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('admin.job_geo_debug.ai_summary_not_processed')}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          loading={isGenerating}
          onClick={onGenerate}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('admin.job_geo_debug.ai_summary_generate')}
        </Button>
        {generationErrorMessage && (
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {generationErrorMessage}
          </Typography>
        )}
      </Box>
    );
  }

  const verdictIsPending = aiJobSummary.verdict === AiJobSummaryVerdict.PENDING;
  const verdictIsFailure = aiJobSummary.verdict === AiJobSummaryVerdict.FAILED;
  const verdictIsSkipped = aiJobSummary.verdict === AiJobSummaryVerdict.SKIPPED_MIN_PINGS;
  const countsMismatch = aiLoadSummaryCount !== platformLoadCount;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 2,
            rowGap: 1.5,
            flex: 1,
            minWidth: 0,
            // aligns the field labels with the section labels below, which are
            // indented 9px to match the accordion content inside the 1px borders
            pl: '9px',
          }}
        >
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_source_label')}
            value={aiJobSummary.source}
          />
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_model_label')}
            value={aiJobSummary.model ?? '—'}
          />
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_prompt_version_label')}
            value={
              aiJobSummary.promptVersion !== null
                ? String(aiJobSummary.promptVersion)
                : '—'
            }
          />
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_verdict_label')}
            value={t(VERDICT_LABEL_KEYS[aiJobSummary.verdict])}
          />
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_confidence_label')}
            value={
              aiJobSummary.confidence !== null ? aiJobSummary.confidence.toFixed(2) : '—'
            }
          />
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_computed_at_label')}
            value={formatAiSummaryTimestamp(aiJobSummary.computedAt)}
          />
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_staging_dwell_label')}
            value={formatAiSummaryMinutes(aiJobSummary.stagingDwellSeconds)}
          />
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_ai_load_count_label')}
            value={String(aiLoadSummaryCount)}
            emphasizeError={countsMismatch}
          />
          <SummaryField
            label={t('admin.job_geo_debug.ai_summary_platform_load_count_label')}
            value={String(platformLoadCount)}
            emphasizeError={countsMismatch}
          />
        </Box>

        {aiJobSummary.resummarizable && (
          <Tooltip title={t('admin.job_geo_debug.ai_summary_regenerate')}>
            {/* span keeps the tooltip working while the button is disabled mid-run */}
            <Box component="span" sx={{ flexShrink: 0 }}>
              <IconButton
                size="small"
                loading={isGenerating}
                onClick={onGenerate}
                aria-label={t('admin.job_geo_debug.ai_summary_regenerate')}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Box>

      {generationErrorMessage && (
        <Typography variant="body2" sx={{ color: 'error.main', pl: '9px' }}>
          {generationErrorMessage}
        </Typography>
      )}

      {aiJobSummary.reasoning && (
        <Box>
          <Label sx={{ mb: 0.5, pl: '9px' }}>
            {t('admin.job_geo_debug.ai_summary_reasoning')}
          </Label>
          <Accordion
            disableGutters
            sx={{
              boxShadow: 'none',
              '&:before': { display: 'none' },
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                minHeight: 36,
                px: 1,
                '& .MuiAccordionSummary-content': {
                  my: 0.5,
                  minWidth: 0,
                  alignItems: 'center',
                },
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {aiJobSummary.reasoning}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1, px: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}
              >
                {aiJobSummary.reasoning}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {verdictIsPending && (
        <Typography variant="body2" sx={{ color: 'text.secondary', pl: '9px' }}>
          {t('admin.job_geo_debug.ai_summary_pending')}
        </Typography>
      )}
      {verdictIsFailure && (
        <Typography variant="body2" sx={{ color: 'error.main', pl: '9px' }}>
          {t('admin.job_geo_debug.ai_summary_failed')}
        </Typography>
      )}
      {verdictIsSkipped && (
        <Typography variant="body2" sx={{ color: 'error.main', pl: '9px' }}>
          {t('admin.job_geo_debug.ai_summary_skipped')}
        </Typography>
      )}
    </Box>
  );
}
